import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import AppHeader from "../components/AppHeader";
import BottomNav from "../components/BottomNav";
import AddressSheet from "../components/AddressSheet";
import { useAppState } from "../context/AppState";
import { api, authenticatedApi } from "../lib/api";
import { useLocale } from "../context/Locale";

export default function AccountPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { address, logout, saveUser, user } = useAppState();
  const { locale: language, setLocale: setLanguage, t } = useLocale();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notificationState, setNotificationState] = useState(
    "Notification" in window ? Notification.permission : "unsupported",
  );
  const [notificationFeedback, setNotificationFeedback] = useState("");

  useEffect(() => {
    if (!user?.auth_token) return;
    authenticatedApi("/update-user-info", user.auth_token, {
      method: "POST",
      body: "{}",
    })
      .then((response) => {
        if (response?.success && response?.data) saveUser(response.data);
      })
      .catch(() => {});
  }, [user?.auth_token]);

  useEffect(() => {
    const refreshPermission = (event) => {
      const permission = event?.detail || (
        "Notification" in window ? Notification.permission : "unsupported"
      );
      setNotificationState(permission);
    };
    window.addEventListener("delivery-huaraz-notification-permission", refreshPermission);
    document.addEventListener("visibilitychange", refreshPermission);
    return () => {
      window.removeEventListener("delivery-huaraz-notification-permission", refreshPermission);
      document.removeEventListener("visibilitychange", refreshPermission);
    };
  }, []);

  function updateField(event) {
    setForm((current) => ({ ...current, [event.target.name]: event.target.value }));
  }

  async function authenticate(event) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const payload = {
        email: form.email.trim(),
        password: form.password,
        ...(mode === "register"
          ? {
              name: form.name.trim(),
              phone: form.phone.trim(),
              ...(address
                ? {
                    address: {
                      lat: address.lat,
                      lng: address.lng,
                      address: address.address,
                      house: "",
                      tag: "Casa",
                    },
                  }
                : {}),
            }
          : {}),
      };
      const response = await api(mode === "login" ? "/login" : "/register", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      if (!response?.success || !response?.data?.auth_token) {
        const message = response?.email_phone_already_used
          ? "El correo o teléfono ya está registrado."
          : response?.data === "DONOTMATCH"
            ? "El correo o la contraseña no coinciden."
            : "No pudimos completar el acceso.";
        throw new Error(message);
      }
      saveUser(response.data);
      navigate(params.get("next") || "/account", { replace: true });
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  }

  async function enableNotifications() {
    setNotificationFeedback("");
    if (!("Notification" in window)) {
      setNotificationState("unsupported");
      return;
    }
    if (Notification.permission === "denied") {
      setNotificationState("denied");
      setNotificationFeedback(t("notificationsBlockedHelp"));
      return;
    }
    try {
      const permission = window.deliveryHuarazEnableNotifications
        ? await window.deliveryHuarazEnableNotifications()
        : await Notification.requestPermission();
      setNotificationState(permission);
      if (permission === "denied") setNotificationFeedback(t("notificationsBlockedHelp"));
      if (permission === "granted") setNotificationFeedback(t("notificationsEnabled"));
    } catch {
      setNotificationFeedback(t("notificationsError"));
    }
  }

  function changeLanguage(event) {
    setLanguage(event.target.value);
  }

  return (
    <main className="food-app">
      {!user && <AppHeader />}
      <div className={user ? "account-page account-page--authenticated" : "account-page"}>
        {!user && <small>{t("profile")}</small>}
        {!user && <h1>{t("myAccount")}</h1>}
        {params.get("password") === "changed" && (
          <div className="wallet-message">Tu contraseña fue actualizada. Ya puedes iniciar sesión.</div>
        )}

        {user ? (
          <>
            <section className="account-profile-legacy">
              <div>
                <h1>{user.name}</h1>
                <span>{user.phone}</span>
                <span>{user.email}</span>
              </div>
              <div className="account-profile-legacy__avatar">{user.name?.charAt(0) || "U"}</div>
            </section>

            <h2 className="account-section-title">{t("myAccount")}</h2>
            <section className="account-language-legacy">
              <div><span>🌐</span><div><b>{t("language")}</b><small>{t("languageHelp")}</small></div></div>
              <select value={language} onChange={changeLanguage} aria-label="Idioma">
                <option value="ES">Español</option>
                <option value="EN">English</option>
                <option value="FR">Français</option>
                <option value="JA">日本語</option>
              </select>
            </section>

            <div className="account-links account-links--legacy">
              <Link to="/addresses"><span>📍</span><div><b>{t("myAddresses")}</b><small>{t("addressHelp")}</small></div><em>›</em></Link>
              <Link to="/orders"><span>🧾</span><div><b>{t("myOrders")}</b><small>{t("history")}</small></div><em>›</em></Link>
              <Link to="/wallet"><span>💳</span><div><b>{t("walletCredits")}</b><small>{t("walletHelp")}</small></div><em>›</em></Link>
              <Link to="/wallet"><span>☆</span><div><b>Créditos Delivery</b><small>Beneficios y saldo promocional</small></div><em>›</em></Link>
              <Link to="/favorites"><span>♥</span><div><b>{t("myFavorites")}</b><small>{t("favoriteHelp")}</small></div><em>›</em></Link>
              <Link to="/pages"><span>💬</span><div><b>Chatear con soporte</b><small>Ayuda de Delivery Huaraz</small></div><em>›</em></Link>
            </div>
            <button className="account-logout" type="button" onClick={logout}>{t("logout")}</button>
          </>
        ) : (
          <section className="auth-card">
            <div className="auth-tabs">
              <button
                type="button"
                className={mode === "login" ? "is-active" : ""}
                onClick={() => setMode("login")}
              >
                {t("login")}
              </button>
              <button
                type="button"
                className={mode === "register" ? "is-active" : ""}
                onClick={() => setMode("register")}
              >
                {t("register")}
              </button>
            </div>
            <form onSubmit={authenticate}>
              {mode === "register" && (
                <>
                  <label>{t("name")}<input name="name" value={form.name} onChange={updateField} required /></label>
                  <label>{t("phone")}<input name="phone" value={form.phone} onChange={updateField} required /></label>
                </>
              )}
              <label>{t("email")}<input type="email" name="email" value={form.email} onChange={updateField} required /></label>
              <label>{t("password")}<input type="password" name="password" minLength={8} value={form.password} onChange={updateField} required /></label>
              {error && <div className="checkout-error">{error}</div>}
              <button className="auth-submit" type="submit" disabled={busy}>
                {busy ? "…" : mode === "login" ? t("enter") : t("registerMe")}
              </button>
              {mode === "login" && <Link className="auth-forgot-link" to="/login/forgot-password">{t("forgotPassword")}</Link>}
            </form>
          </section>
        )}

        <div className={user ? "account-links account-links--help" : "account-links"}>
          <Link to="/pages"><span>ℹ️</span><div><b>{t("helpInfo")}</b><small>{t("helpInfoDesc")}</small></div><em>›</em></Link>
        </div>

        {!user && <section className="preference-card">
          <div>
            <span>🌐</span>
            <div><b>{t("language")}</b><small>{t("languageHelp")}</small></div>
          </div>
          <select value={language} onChange={changeLanguage} aria-label="Idioma">
            <option value="ES">Español</option>
            <option value="EN">English</option>
            <option value="FR">Français</option>
            <option value="JA">日本語</option>
          </select>
        </section>}

        <section
          className={`preference-card preference-card--notification ${notificationState === "granted" ? "is-enabled" : "is-actionable"}`}
          onClick={notificationState !== "granted" ? enableNotifications : undefined}
        >
          <div>
            <span>🔔</span>
            <div>
              <b>{t("notifications")}</b>
              <small>
                {notificationState === "granted"
                  ? t("notificationsEnabled")
                  : notificationState === "denied"
                    ? t("notificationsBlocked")
                    : notificationState === "unsupported"
                      ? t("notificationsUnsupported")
                      : t("notificationsHelp")}
              </small>
            </div>
          </div>
          {notificationState !== "granted" && notificationState !== "unsupported" && (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                enableNotifications();
              }}
            >
              {notificationState === "denied" ? t("review") : t("activate")}
            </button>
          )}
          {notificationState === "granted" && <em>✓</em>}
          {notificationFeedback && (
            <p className={notificationState === "granted" ? "is-success" : ""} aria-live="polite">
              {notificationFeedback}
            </p>
          )}
        </section>
      </div>
      <BottomNav />
      <AddressSheet />
    </main>
  );
}
